import { ApplicantFinalizeExamStatus, ExaminerProfile, ExamStatsTab } from '../../../../../../../store/workspace/types';
import { finalizeApplicantExamStatusThunk } from '../../../../../../../store/workspace/thunks';
import { ChevronDownIcon, ProfileIcon } from '../../../../../../../assets/icons';
import { examStatusTitle } from '../../../../../../../utils/generalHelpers';
import { setNotification } from '../../../../../../../store/general/slice';
import { finalResultActions } from '../../../../../../../configs';
import { useDispatch } from '../../../../../../../store/store';
import Button from '../../../../../../../components/Button';
import { ForwardedRef, forwardRef, useState } from 'react';
import { useExpandableRows } from '../../../../hooks/useExpandableRows';
import { useExaminerProfile } from '../../../../hooks/useExaminerProfile';
import styles from './style.module.scss';

interface Props {
    jobId: string | undefined;
    examinersData: ExaminerProfile[];
    fetchTablesData: (tab: ExamStatsTab) => void;
};

const FinalResultTable = forwardRef((props: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const { jobId, examinersData, fetchTablesData } = props;

    const dispatch = useDispatch();
    const openExaminerProfile = useExaminerProfile(jobId);
    const { isExpanded, toggleRow } = useExpandableRows<string>();
    /** The status change currently in flight, so only its button spins. */
    const [pending, setPending] = useState<{ applicantId: string; action: ApplicantFinalizeExamStatus } | null>(null);

    const finalizeApplicantExamStatus = (applicantId: string, finalize: ApplicantFinalizeExamStatus) => {
        if (!jobId) return;

        setPending({ applicantId, action: finalize });
        dispatch(finalizeApplicantExamStatusThunk({ applicantId, finalize, jobId }))
            .unwrap()
            .then(() => {
                dispatch(setNotification(['Applicant exam status updated successfully', 'success']));
                fetchTablesData('finalResult');
            })
            .catch(() => {
                dispatch(setNotification(['Something went wrong,Try again...', 'error']));
            })
            .finally(() => {
                setPending(null);
            });
    };

    const renderActions = (applicant: ExaminerProfile, className?: string) =>
        finalResultActions.map(action => <Button
            key={action.id}
            className={className}
            text={action.title}
            disabled={pending !== null}
            loading={pending?.applicantId === applicant.userId && pending.action === action.id}
            type={applicant.finalize === action.id ? 'Primary' : 'Outline-Primary'}
            onClick={() => finalizeApplicantExamStatus(applicant.userId, action.id)}
        />);

    return (
        <div className={styles.wrapper}>
            <div className={styles.table} ref={ref}>
                <table>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Status</th>
                            <th>Interview status (Please select the status)</th>
                            <th data-noprint='true'>Info</th>
                        </tr>
                    </thead>
                    <tbody>
                        {examinersData.length > 0
                            ? examinersData.map((applicant, index) => <tr key={applicant.userId}>
                                <td>{index + 1}</td>
                                <td>
                                    <div className={styles.examinerName}>
                                        <span>{applicant.first_name} {applicant.family_name}</span>
                                    </div>
                                </td>
                                <td>
                                    <span className={styles.status} data-status={applicant.status}>
                                        {examStatusTitle(applicant.status)}
                                    </span>
                                </td>
                                <td>
                                    <div className={styles.actions}>
                                        {renderActions(applicant, styles.actions_btn)}
                                    </div>
                                </td>
                                <td data-noprint='true' onClick={() => openExaminerProfile(applicant.userId)}>
                                    <ProfileIcon />
                                </td>
                            </tr>)
                            : <tr><td colSpan={5}>No data.</td></tr>}
                    </tbody>
                </table>
            </div>

            <div className={styles.mobile_list}>
                {examinersData.map(applicant => <div className={styles.card} key={applicant.userId}>
                    <div className={styles.card_header} onClick={() => toggleRow(applicant.userId)}>
                        <span>{applicant.first_name} {applicant.family_name}</span>
                        <ChevronDownIcon style={{ transform: isExpanded(applicant.userId) ? 'rotate(180deg)' : 'none' }} />
                    </div>

                    {isExpanded(applicant.userId) && <div className={styles.card_body}>
                        <div className={styles.card_body_section}>
                            <div className={styles.card_body_section_title}>
                                <span>Status</span>
                                <span className={styles.status} data-status={applicant.status}>
                                    {examStatusTitle(applicant.status)}
                                </span>
                            </div>
                        </div>
                        <div className={styles.card_body_section}>
                            <div className={styles.card_body_section_title}>
                                <span>Interview status (Please select the status)</span>
                            </div>
                            <div className={styles.card_body_section_content}>
                                {renderActions(applicant)}
                            </div>
                        </div>
                    </div>}
                </div>)}
            </div>
        </div>
    );
});

export default FinalResultTable;
